import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIssueState} from '@github-ui/issue-pr-state'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {ActionList} from '@primer/react'
import type React from 'react'

import {useCachePreheatIssueViewer} from '../../../hooks/use-cache-preheat-issueviewer'
import type {DependencyIssueNode, DependencyIssueRepositoryNode} from './dependency-issue-query-types'

// Fields `DependencyIssue` reads off a `blockedBy`/`blocking` issue node, derived from the allowlisted
// `IssueViewerSecondaryViewQuery` types.
// RELAY-CLEANUP(RelationshipsSection): `RelayCompat` widens nullable fields so the still-Relay
// RelationshipsSection parent data is assignable. Remove the wrapper when RelationshipsSection leaves Relay.
export type DependencyIssueFragment = RelayCompat<
  Pick<DependencyIssueNode, 'title' | 'titleHTML' | 'url' | 'number' | 'state' | 'stateReason'> & {
    readonly repository: Pick<DependencyIssueRepositoryNode, 'nameWithOwner' | 'name'> & {
      readonly owner: {readonly login: string}
    }
  }
>

type DependencyIssueProps = {
  issue: DependencyIssueFragment
  onLinkClick?: (event: MouseEvent) => void
}

export function DependencyIssue({issue: data, onLinkClick}: DependencyIssueProps) {
  useCachePreheatIssueViewer({
    owner: data.repository.owner.login,
    repo: data.repository.name,
    number: data.number,
    source: 'DependencyIssue',
    tabbedTimeline: isFeatureEnabled('issues_react_timeline_tabs'),
  })

  // TODO: Add subscription for dependency issues
  // useDependencyIssueSubscription(data.id)
  const {sourceIcon} = useIssueState({
    state: data.state,
    stateReason: data.stateReason,
  })

  const IconComponent = sourceIcon('Issue')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
  const title = (data.titleHTML || data.title) as SafeHTMLString

  return (
    <ActionList.LinkItem
      href={data.url}
      target="_blank"
      onClick={
        onLinkClick
          ? (event: React.MouseEvent<HTMLElement>) => {
              onLinkClick(event.nativeEvent)
            }
          : undefined
      }
    >
      <ActionList.LeadingVisual>
        {/* eslint-disable-next-line react-hooks/static-components */}
        <IconComponent />
      </ActionList.LeadingVisual>
      <VerifiedHTMLText html={title} className="markdown-title" />
      <ActionList.Description variant="block">
        {data.repository.nameWithOwner}#{data.number}
      </ActionList.Description>
    </ActionList.LinkItem>
  )
}
